import React, { useState, useMemo, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Quotation, CreateQuotationData, QuotationItem } from '../../types/quotation';
import { Product } from '../../types/product';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useCustomers } from '../../hooks/useCustomers';
import { useProducts } from '../../hooks/useProducts';
import { useQuotations } from '../../hooks/useQuotations';
import Modal from '../common/Modal';
import { DatePicker } from '../common/DatePicker';
import {
  BuildingOffice2Icon,
  UserIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  PlusIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  IdentificationIcon,
  ShoppingCartIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { generateQuotationNumber } from '../../utils/formatters';
import { formatDocumentDate } from '../../utils/formatters';
import { Combobox, Portal } from '@headlessui/react';
import { ChevronUpDownIcon as ChevronUpDownIconSolid } from '@heroicons/react/20/solid';
import { useLanguage } from '../../contexts/LanguageContext';

interface QuotationFormSimpleProps {
  onSubmit: (data: CreateQuotationData) => void;
  onCancel: () => void;
  isOpen: boolean;
  initialData?: Quotation;
}

interface ProductSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProduct: (product: Product) => void;
  products: Product[];
}

const ProductSearchModal: React.FC<ProductSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectProduct,
  products
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState('');
  
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    
    const query = searchQuery.toLowerCase();
    return products.filter(product =>
      product.name.toLowerCase().includes(query) ||
      product.code.toLowerCase().includes(query) ||
      product.description?.toLowerCase().includes(query)
    );
  }, [products, searchQuery]);

  const handleProductSelect = (product: Product) => {
    onSelectProduct(product);
    onClose();
    setSearchQuery('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('quotations.form.productSearch.title')}
      size="4xl"
    >
      <div className="p-6">
        {/* Search Input */}
        <div className="mb-4">
          <div className="relative">
            <input
              type="text"
              placeholder={t('quotations.form.productSearch.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pl-10 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500 absolute left-3 top-1/2 transform -translate-y-1/2" />
          </div>
        </div>

        {/* Products List */}
        <div className="max-h-[32rem] overflow-y-auto">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <ShoppingCartIcon className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
              <p>{searchQuery ? t('quotations.form.productSearch.noResults') : t('quotations.form.productSearch.noProducts')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  onClick={() => handleProductSelect(product)}
                  className="flex items-center p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 cursor-pointer transition-all"
                >
                  <div className="p-2 rounded bg-gradient-to-br from-green-100 to-teal-200 dark:from-green-800 dark:to-teal-700 mr-3 flex-shrink-0">
                    <ShoppingCartIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                        {product.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                        {product.code}
                      </div>
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1 truncate">
                      {product.description}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {product.unit} • {product.taxRate || '27%'} ÁFA
                      </div>
                      {product.netPrice && (
                        <div className="text-sm font-medium text-green-600 dark:text-green-400">
                          {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(product.netPrice)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const QuotationFormSimple: React.FC<QuotationFormSimpleProps> = ({ onSubmit, onCancel, isOpen, initialData }) => {
  const { t } = useLanguage();
  const { organizations } = useOrganizations();
  const { customers } = useCustomers();
  const { products } = useProducts();
  const { quotations } = useQuotations();
  const [orgQuery, setOrgQuery] = useState('');
  const [customerQuery, setCustomerQuery] = useState('');
  const [isProductSearchOpen, setIsProductSearchOpen] = useState(false);

  const lastQuotationNumber = quotations
    .map(q => q.number)
    .sort()
    .reverse()[0];

  const defaultValues: CreateQuotationData = {
    status: 'draft',
    number: generateQuotationNumber(lastQuotationNumber, 'Minta', [], ''),
    organizationId: '',
    customerId: '',
    quotationNumber: '',
    companyName: '',
    companyAddress: '',
    taxNumber: '',
    registrationNumber: '',
    representativeName: '',
    contactName: '',
    date: new Date().toISOString().split('T')[0],
    validUntil: '',
    paymentDue: '15',
    
    // Basic pricing information
    subtotal: 0,
    taxAmount: 0,
    totalAmount: 0,
    currency: 'HUF',
    
    documents: [],
    items: [],
    publicToken: '',
    notes: ''
  };

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<CreateQuotationData>({
    defaultValues
  });

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      const formData = initialData || defaultValues;
      reset(formData);
    }
  }, [isOpen, initialData, reset]);

  const selectedOrgId = watch('organizationId');
  const selectedOrg = organizations.find(org => org.id === selectedOrgId);

  const filteredOrganizations = useMemo(() => {
    return organizations.filter(org =>
      org.name.toLowerCase().includes(orgQuery.toLowerCase())
    );
  }, [organizations, orgQuery]);

  const filteredCustomers = useMemo(() => {
    return customers.filter(customer => {
      const searchStr = customer.type === 'organization'
        ? customer.companyName?.toLowerCase()
        : `${customer.lastName} ${customer.firstName}`.toLowerCase();
      
      return searchStr?.includes(customerQuery.toLowerCase()) ||
        customer.email?.toLowerCase().includes(customerQuery.toLowerCase());
    });
  }, [customers, customerQuery]);

  const selectedCustomerId = watch('customerId');
  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);

  // Auto-fill customer data when selected
  useEffect(() => {
    if (selectedCustomer && selectedCustomer.type === 'organization') {
      setValue('companyName', selectedCustomer.companyName || '');
      setValue('taxNumber', selectedCustomer.taxNumber || '');
      setValue('registrationNumber', selectedCustomer.registrationNumber || '');
      
      if (selectedCustomer.address) {
        const addressParts = [];
        if (selectedCustomer.address.street) addressParts.push(selectedCustomer.address.street);
        if (selectedCustomer.address.city) addressParts.push(selectedCustomer.address.city);
        if (selectedCustomer.address.postalCode) addressParts.push(selectedCustomer.address.postalCode);
        if (selectedCustomer.address.country) addressParts.push(selectedCustomer.address.country);
        
        const fullAddress = addressParts.join(', ');
        setValue('companyAddress', fullAddress);
      }
      
      if (selectedCustomer.contactPerson && selectedCustomer.contactPerson.name) {
        setValue('contactName', selectedCustomer.contactPerson.name);
      }
    }
  }, [selectedCustomerId, selectedCustomer, setValue]);

  // Product items management
  const handleAddItem = () => {
    const newItem: QuotationItem = {
      productId: '',
      productName: '',
      productCode: '',
      quantity: 1,
      unitPrice: 0,
      price: 0,
      taxRate: 27,
      lineTotal: 0,
      lineTotalWithTax: 0,
      currency: 'HUF'
    };
    
    const currentItems = watch('items') || [];
    setValue('items', [...currentItems, newItem]);
  };

  const handleAddProductFromSearch = () => {
    setIsProductSearchOpen(true);
  };

  const handleProductSelect = (product: Product) => {
    const netPrice = Number(product.netPrice || 0);
    const taxRate = product.taxRate === '27%' ? 27 : product.taxRate === '18%' ? 18 : product.taxRate === '5%' ? 5 : 27;
    
    const newItem: QuotationItem = {
      productId: product.id,
      productName: product.name,
      productCode: product.code,
      quantity: 1,
      unitPrice: netPrice,
      price: netPrice,
      taxRate: taxRate,
      lineTotal: netPrice,
      lineTotalWithTax: Number((netPrice * (1 + taxRate / 100)).toFixed(2)),
      currency: 'HUF'
    };
    
    const currentItems = watch('items') || [];
    setValue('items', [...currentItems, newItem]);
    calculateTotals([...currentItems, newItem]);
  };

  const handleRemoveItem = (index: number) => {
    const currentItems = watch('items') || [];
    const updatedItems = currentItems.filter((_, i) => i !== index);
    setValue('items', updatedItems);
    calculateTotals(updatedItems);
  };

  const calculateTotals = (items: QuotationItem[]) => {
    const subtotal = items.reduce((sum, item) => sum + Number(item.lineTotal || 0), 0);
    const taxAmount = items.reduce((sum, item) => sum + (Number(item.lineTotalWithTax || 0) - Number(item.lineTotal || 0)), 0);
    const totalAmount = subtotal + taxAmount;
    
    setValue('subtotal', subtotal);
    setValue('taxAmount', taxAmount);
    setValue('totalAmount', totalAmount);
  };

  const handleItemChange = (index: number, field: keyof QuotationItem, value: any) => {
    const currentItems = watch('items') || [];
    const updatedItems = [...currentItems];
    
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    
    // Calculate line totals with improved logic
    const item = updatedItems[index];
    if (field === 'quantity' || field === 'unitPrice' || field === 'price' || field === 'taxRate' || field === 'discount') {
      // Biztosítjuk, hogy számként kezeljük az értékeket
      const basePrice = Number(item.unitPrice || item.price || 0);
      const quantity = Number(item.quantity || 1);
      const discount = Number(item.discount || 0);
      const taxRate = Number(item.taxRate || 0);
      
      // Sync price with unitPrice
      item.price = basePrice;
      
      // Calculate with discount
      const discountAmount = basePrice * (discount / 100);
      const discountedPrice = basePrice - discountAmount;
      
      // Calculate totals - biztosítjuk, hogy számként tároljuk
      item.lineTotal = Number((quantity * discountedPrice).toFixed(2));
      item.lineTotalWithTax = Number((item.lineTotal * (1 + taxRate / 100)).toFixed(2));
    }
    
    setValue('items', updatedItems);
    calculateTotals(updatedItems);
  };

  const handleFormSubmit = (data: CreateQuotationData) => {
    // Ensure items have proper calculations
    if (data.items && data.items.length > 0) {
      data.items = data.items.map(item => {
        const basePrice = Number(item.unitPrice || item.price || 0);
        const quantity = Number(item.quantity || 1);
        const discount = Number(item.discount || 0);
        const taxRate = Number(item.taxRate || 0);
        
        // Calculate with discount
        const discountAmount = basePrice * (discount / 100);
        const discountedPrice = basePrice - discountAmount;
        
        return {
          ...item,
          price: basePrice,
          lineTotal: Number((quantity * discountedPrice).toFixed(2)),
          lineTotalWithTax: Number((quantity * discountedPrice * (1 + taxRate / 100)).toFixed(2))
        };
      });
      
      calculateTotals(data.items);
    }
    
    onSubmit(data);
  };

  const currentItems = watch('items') || [];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? t('quotations.form.title.edit') : t('quotations.form.title.create')}
      size="6xl"
    >
      <div className="relative p-8">
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-md">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-700">
                <DocumentTextIcon className="w-5 h-5 text-blue-700" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">
                  {t('quotations.form.sections.basic.title')}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative z-10">
                {/* Organization Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('quotations.form.fields.organization')} <span className="text-red-500">*</span>
                  </label>
                  <Combobox
                    value={watch('organizationId')}
                    onChange={(orgId) => setValue('organizationId', orgId)}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        onChange={event => setOrgQuery(event.target.value)}
                        displayValue={(orgId: string) => {
                          const org = organizations.find(o => o.id === orgId);
                          return org ? org.name : '';
                        }}
                        placeholder={t('common.placeholders.selectOrganization')}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </Combobox.Button>
                      <Combobox.Options className="absolute z-[9999] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none">
                        {filteredOrganizations.map(org => (
                          <Combobox.Option
                            key={org.id}
                            value={org.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100' : 'text-gray-800 dark:text-gray-200'}`
                            }
                          >
                            {org.name}
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </div>
                  </Combobox>
                </div>

                {/* Customer Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('quotations.form.fields.customer')} <span className="text-red-500">*</span>
                  </label>
                  <Combobox
                    value={watch('customerId')}
                    onChange={(customerId) => setValue('customerId', customerId)}
                  >
                    <div className="relative">
                      <Combobox.Input
                        className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                        onChange={event => setCustomerQuery(event.target.value)}
                        displayValue={(customerId: string) => {
                          const customer = customers.find(c => c.id === customerId);
                          if (!customer) return '';
                          return customer.type === 'organization'
                            ? customer.companyName || ''
                            : `${customer.lastName} ${customer.firstName}`;
                        }}
                        placeholder={t('common.placeholders.selectCustomer')}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                      </Combobox.Button>
                      <Combobox.Options className="absolute z-[9999] mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 shadow-lg ring-1 ring-black dark:ring-gray-700 ring-opacity-5 focus:outline-none">
                        {filteredCustomers.map(customer => (
                          <Combobox.Option
                            key={customer.id}
                            value={customer.id}
                            className={({ active }) =>
                              `relative cursor-pointer select-none py-2 px-4 ${active ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100' : 'text-gray-800 dark:text-gray-200'}`
                            }
                          >
                            <div className="flex items-center space-x-3">
                              <div className={`p-1 rounded ${customer.type === 'organization' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-purple-100 dark:bg-purple-900/30'}`}>
                                {customer.type === 'organization' ? (
                                  <BuildingOffice2Icon className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                ) : (
                                  <UserIcon className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium">
                                  {customer.type === 'organization'
                                    ? customer.companyName
                                    : `${customer.lastName} ${customer.firstName}`}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {customer.email}
                                </div>
                              </div>
                            </div>
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </div>
                  </Combobox>
                </div>

                {/* Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('quotations.form.fields.date')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...register('date', { required: true })}
                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* Valid Until */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('quotations.form.fields.validUntil')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...register('validUntil', { required: true })}
                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                  />
                </div>

                {/* Payment Due */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fizetési határidő (nap)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="365"
                    {...register('paymentDue')}
                    className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                    placeholder="15"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Items Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-md">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-green-300 to-teal-400 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-700">
              <ShoppingCartIcon className="w-5 h-5 text-green-700" />
            </div>
            <div className="p-4 flex-1">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {t('quotations.form.sections.items.title')}
                </h3>
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={handleAddProductFromSearch}
                    className="flex items-center px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-md transition-colors"
                  >
                    <MagnifyingGlassIcon className="w-4 h-4 mr-1" />
                    Termék keresése
                  </button>
                  <button
                    type="button"
                    onClick={handleAddItem}
                    className="flex items-center px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md transition-colors"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Üres tétel
                  </button>
                </div>
              </div>

              {currentItems.length > 0 ? (
                <div className="space-y-4">
                  {currentItems.map((item, index) => (
                    <div key={index} className="p-4 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t('quotations.form.sections.items.item')} {index + 1}
                          </h4>
                          {item.productId && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                              <CheckIcon className="w-3 h-3 mr-1" />
                              Termék
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-1 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div className="lg:col-span-2">
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Termék neve
                          </label>
                          <input
                            type="text"
                            value={item.productName}
                            onChange={(e) => handleItemChange(index, 'productName', e.target.value)}
                            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                            placeholder="Termék neve"
                          />
                          {item.productCode && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              Kód: {item.productCode}
                            </div>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Mennyiség
                          </label>
                          <input
                            type="number"
                            min="1"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 1)}
                            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Egységár (HUF)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.unitPrice || item.price || 0}
                            onChange={(e) => handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            ÁFA (%)
                          </label>
                          <select
                            value={item.taxRate}
                            onChange={(e) => handleItemChange(index, 'taxRate', parseFloat(e.target.value))}
                            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                          >
                            <option value={27}>27%</option>
                            <option value={18}>18%</option>
                            <option value={5}>5%</option>
                            <option value={0}>0%</option>
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Kedvezmény (%)
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={item.discount || 0}
                            onChange={(e) => handleItemChange(index, 'discount', parseFloat(e.target.value) || 0)}
                            className="w-full bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
                            placeholder="0"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Nettó összeg
                          </label>
                          <div className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100">
                            {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(item.lineTotal)}
                            {item.discount && item.discount > 0 && (
                              <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                                -{item.discount}% kedvezmény
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                            Bruttó összeg
                          </label>
                          <div className="w-full bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                            {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(item.lineTotalWithTax)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <ShoppingCartIcon className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
                  <p className="mb-3">Még nincsenek termékek hozzáadva</p>
                  <div className="flex justify-center space-x-2">
                    <button
                      type="button"
                      onClick={handleAddProductFromSearch}
                      className="flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-md transition-colors"
                    >
                      <MagnifyingGlassIcon className="w-4 h-4 mr-2" />
                      Termék keresése
                    </button>
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="flex items-center px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-md transition-colors"
                    >
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Üres tétel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Section */}
        {currentItems.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-md">
            <div className="flex">
              <div className="p-4 bg-gradient-to-br from-purple-300 to-violet-400 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-700">
                <CurrencyDollarIcon className="w-5 h-5 text-purple-700" />
              </div>
              <div className="p-4 flex-1">
                <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">
                  {t('quotations.form.sections.summary')}
                </h3>
                
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('quotations.fields.subtotal')}:</span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">
                      {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(watch('subtotal') || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('quotations.fields.tax')}:</span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">
                      {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(watch('taxAmount') || 0)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-t border-gray-200 dark:border-gray-600 pt-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('quotations.fields.grandTotal')}:</span>
                    <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                      {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(watch('totalAmount') || 0)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Notes Section */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-md">
          <div className="flex">
            <div className="p-4 bg-gradient-to-br from-amber-300 to-orange-400 flex items-center justify-center rounded-l-lg border-r border-gray-200 dark:border-gray-700">
              <DocumentTextIcon className="w-5 h-5 text-amber-700" />
            </div>
            <div className="p-4 flex-1">
              <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-4">
                {t('quotations.form.sections.notes.title')}
              </h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('quotations.form.fields.notes')}
                </label>
                <textarea
                  {...register('notes')}
                  rows={4}
                  className="w-full bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 resize-vertical"
                  placeholder={t('common.placeholders.quotationNotes')}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
          >
            {t('common.buttons.cancel')}
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
          >
            {initialData ? t('common.buttons.save') : t('common.buttons.create')}
          </button>
        </div>
      </form>

      {isProductSearchOpen && (
        <ProductSearchModal
          isOpen={isProductSearchOpen}
          onClose={() => setIsProductSearchOpen(false)}
          onSelectProduct={handleProductSelect}
          products={products}
        />
      )}
      </div>
    </Modal>
  );
};

export default QuotationFormSimple; 